import { FC, useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Divider,
  Dialog,
  DialogContent,
  CircularProgress,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Print as PrintIcon,
  AutoAwesome as AutoAwesomeIcon,
  LocalHospital as HospitalIcon,
  Check as CheckIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Image as ImageIcon,
  Close as RemoveIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { sanitizeDischargeHtml } from '../../utils/sanitizeHtml';
import { INSTANT_FORMATS } from '../../utils/dateUtils';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { DischargeSummary } from '../../types/dischargeSummary';
import { DischargeSummaryService } from '../../services/dischargeSummaryService';
import DischargeSummaryEditor from './DischargeSummaryEditor';
import AutomationSuggestionsModal from './AutomationSuggestionsModal';
import ImagingStudyPicker from './ImagingStudyPicker';
import AttachedImagingBlock from './AttachedImagingBlock';
import { useRBAC } from '../../auth/useRBAC';
import api from '../../utils/api';
import { useAnalytics } from '../../hooks/useAnalytics';
import { executeWrite, executeUpdate, executeDelete, OfflineWriteBlockedError } from '../../offline/executeWrite';
import { withOfflineFallback } from '../../offline/offlineQuery';
import { useIsOffline } from '../../offline/OfflineNotice';
import { readByHorse } from '../../offline/readers';

interface Props {
  horseId: string;
  /**
   * When provided, the component opens directly on this summary once the list
   * loads — used when a discharge is clicked/edited from the clinical timeline
   * so the user lands on that draft instead of the list/create view. Applied
   * once per distinct id, so "Back to List" still works.
   */
  initialSummaryId?: string;
  /**
   * When true (with a DRAFT initialSummaryId), open straight into the editor
   * instead of the read view — used by the timeline's inline "Edit" action.
   */
  initialEditMode?: boolean;
}

const DischargeSummarySimplified: FC<Props> = ({ horseId, initialSummaryId, initialEditMode }) => {
  const queryClient = useQueryClient();
  const { hasPermission } = useRBAC();
  const { enqueueSnackbar } = useSnackbar();
  const { trackFeatureUsed } = useAnalytics();
  // Reactive connectivity — re-renders on flips so disabled states and query
  // enabled gates recover after reconnect (render-time skipWhenOffline() went stale).
  const isOffline = useIsOffline();
  // Every date this screen renders is an INSTANT (DATE-KINDS §1.3):
  // DischargeSummary.admissionDateTime / dischargeDateTime are written as
  // `new Date().toISOString()` right here, and a Keystone studyDate is Asteris's
  // StudyDateTime. All of them render in the CLINIC's zone — the browser's zone
  // is right only for someone standing in the clinic, and the printed PDF
  // (discharge-summaries.service / pdf.service) already uses Clinic.timezone.
  const formatInstant = useFormatInstant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [selectedSummary, setSelectedSummary] = useState<DischargeSummary | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAutomationModalOpen, setIsAutomationModalOpen] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [preparingPrint, setPreparingPrint] = useState(false);
  const [imagingPickerOpen, setImagingPickerOpen] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [formData, setFormData] = useState<Partial<DischargeSummary>>({
    horseId,
    status: 'DRAFT',
    admissionDateTime: new Date().toISOString(),
    dischargeDateTime: new Date().toISOString(),
    content: '',
    attendingVet: '',
  });

  // Fetch discharge summaries for this horse
  const { data: summaries, isLoading } = useQuery(
    ['discharge-summaries', horseId],
    withOfflineFallback(
      () => DischargeSummaryService.getByHorseId(horseId),
      // Offline drafts created via executeWrite land in the dischargeSummaries
      // replica table — read from it so they render (and existing summaries stay
      // visible) when offline.
      () => readByHorse('dischargeSummaries', horseId) as any,
    ),
    {
      onError: (error) => {
        console.error('Error fetching discharge summaries:', error);
      },
    }
  );

  // Open directly on a summary when launched from the clinical timeline with an
  // initialSummaryId. Applied once per distinct id (tracked via a ref) so that
  // clicking "Back to List" doesn't immediately re-select it, while clicking a
  // different draft on the timeline (new id) still opens that one.
  const appliedInitialIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialSummaryId) return;
    if (appliedInitialIdRef.current === initialSummaryId) return;
    if (!summaries || summaries.length === 0) return;
    const match = summaries.find((s: DischargeSummary) => s.id === initialSummaryId);
    if (!match) return;
    appliedInitialIdRef.current = initialSummaryId;
    setSelectedSummary(match);
    if (initialEditMode && match.status === 'DRAFT') {
      // Opened from an explicit "Edit" affordance on a draft — land in the
      // editor (mirrors handleEdit) instead of the read view.
      setFormData({
        horseId: match.horseId,
        status: match.status,
        admissionDateTime: match.admissionDateTime,
        dischargeDateTime: match.dischargeDateTime,
        content: sanitizeDischargeHtml(match.content),
        attendingVet: match.attendingVet || '',
        finalizedAt: match.finalizedAt,
        finalizedBy: match.finalizedBy,
      });
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }
  }, [initialSummaryId, initialEditMode, summaries]);

  // Create discharge summary
  const createSummary = useMutation(
    (data: Omit<DischargeSummary, 'id' | 'horse' | 'createdAt' | 'updatedAt' | 'clinicName' | 'clinicPhone' | 'clinicEmail' | 'clinicAddress'>) =>
      executeWrite<DischargeSummary>({
        entityType: 'dischargeSummary',
        table: 'dischargeSummaries',
        payload: data as Record<string, any>,
        networkFn: () => api.post('/discharge-summaries', data).then((r) => r.data),
      }),
    {
      onSuccess: () => {
        trackFeatureUsed('discharge_summary_created');
        queryClient.invalidateQueries(['discharge-summaries', horseId]);
        setIsEditMode(false);
        setSelectedSummary(null);
      },
      onError: (error) => {
        // The durability gate refused to capture this write offline — surface
        // the actionable message. (Never fires online → no behavior change.)
        if (error instanceof OfflineWriteBlockedError) {
          alert(error.message);
        }
      },
    }
  );

  // Update discharge summary
  const updateSummary = useMutation(
    ({ id, data }: { id: string; data: Partial<Omit<DischargeSummary, 'id' | 'horseId' | 'horse' | 'createdAt' | 'updatedAt' | 'clinicName' | 'clinicPhone' | 'clinicEmail' | 'clinicAddress'>> }) =>
      executeUpdate<DischargeSummary>({
        entityType: 'dischargeSummary',
        table: 'dischargeSummaries',
        entityId: id,
        payload: data as Record<string, any>,
        networkFn: () => api.put(`/discharge-summaries/${id}`, data).then((r) => r.data),
      }),
    {
      onSuccess: (updatedSummary) => {
        queryClient.invalidateQueries(['discharge-summaries', horseId]);
        setIsEditMode(false);
        setSelectedSummary(updatedSummary);
      },
      onError: (error) => {
        // Offline durability gate refused this draft save — surface it.
        // (Never fires online → no behavior change.)
        if (error instanceof OfflineWriteBlockedError) {
          alert(error.message);
        }
      },
    }
  );

  // Delete discharge summary. Routed through executeDelete so it works offline
  // too: dischargeSummary is replicated, and deleting an un-synced offline
  // draft create-coalesces (cancels the queued create — no server op, no
  // permanent 404 against a temp id).
  const deleteSummary = useMutation(
    (id: string) =>
      executeDelete<void>({
        entityType: 'dischargeSummary',
        table: 'dischargeSummaries',
        entityId: id,
        networkFn: () => DischargeSummaryService.delete(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['discharge-summaries', horseId]);
        setSelectedSummary(null);
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error instanceof OfflineWriteBlockedError
            ? error.message
            : 'Failed to delete discharge summary',
          { variant: 'error' },
        );
      },
    }
  );

  // Attached imaging studies for the currently-open summary. Loaded in both
  // edit mode (so the picker knows what's already attached and the preview
  // block renders) and read-only/finalized mode (so the block renders below
  // the content). Finalized summaries fall back to imagingStudiesSnapshot if
  // present — see the computed `resolvedStudies` below.
  const summaryIdForStudies = selectedSummary ? selectedSummary.id : null;
  // Keystone-backed (online-only) — gated offline, and skipped for offline-
  // created temp-id drafts (_pendingSync) that don't exist server-side yet.
  const { data: attachedStudies = [], refetch: refetchStudies } = useQuery<any[]>(
    ['discharge-summary-studies', summaryIdForStudies],
    () => DischargeSummaryService.listStudies(summaryIdForStudies!),
    {
      enabled:
        !!summaryIdForStudies &&
        !isOffline &&
        !(selectedSummary as any)?._pendingSync,
    },
  );

  // For finalized summaries, prefer the snapshot so the rendered output never
  // changes if the Keystone mirror later drops rows. Falls back to the live
  // relation for drafts.
  const resolvedStudies: any[] = useMemo(() => {
    const snapshot = (selectedSummary as any)?.imagingStudiesSnapshot;
    if (selectedSummary?.status === 'FINALIZED' && Array.isArray(snapshot) && snapshot.length > 0) {
      return snapshot;
    }
    return attachedStudies;
  }, [selectedSummary, attachedStudies]);

  const attachStudiesMutation = useMutation(
    async (studies: any[]) => {
      if (!summaryIdForStudies) return [];
      return DischargeSummaryService.attachStudies(
        summaryIdForStudies,
        studies.map(s => ({ studyId: s.studyId, accessionNumber: s.accessionNumber })),
      );
    },
    {
      onSuccess: () => refetchStudies(),
      onError: () => {
        enqueueSnackbar('Failed to attach imaging studies', { variant: 'error' });
      },
    },
  );

  const detachStudyMutation = useMutation(
    async (studyId: string) => {
      if (!summaryIdForStudies) return [];
      return DischargeSummaryService.detachStudy(summaryIdForStudies, studyId);
    },
    {
      onSuccess: () => refetchStudies(),
      onError: () => {
        enqueueSnackbar('Failed to remove the imaging study', { variant: 'error' });
      },
    },
  );

  // Finalize discharge summary
  const finalizeSummary = useMutation(
    (id: string) => DischargeSummaryService.finalize(id),
    {
      onSuccess: (finalizedSummary) => {
        queryClient.invalidateQueries(['discharge-summaries', horseId]);
        setSelectedSummary(finalizedSummary);
      },
    }
  );

  const handleSave = () => {
    if (selectedSummary) {
      updateSummary.mutate({
        id: selectedSummary.id,
        data: formData,
      });
    } else {
      createSummary.mutate(formData as any);
    }
  };

  const handleEdit = (summary: DischargeSummary) => {
    setSelectedSummary(summary);
    setFormData({
      horseId: summary.horseId,
      status: summary.status,
      admissionDateTime: summary.admissionDateTime,
      dischargeDateTime: summary.dischargeDateTime,
      content: sanitizeDischargeHtml(summary.content),
      attendingVet: summary.attendingVet || '',
      finalizedAt: summary.finalizedAt,
      finalizedBy: summary.finalizedBy,
    });
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setSelectedSummary(null);
    setFormData({
      horseId,
      status: 'DRAFT',
      admissionDateTime: new Date().toISOString(),
      dischargeDateTime: new Date().toISOString(),
      content: '',
      attendingVet: '',
    });
  };

  // Sanitize AI-generated content to remove code fences
  const sanitizeAIContent = (content: string): string => {
    if (!content) return '';
    return content
      .replace(/^```html\n?/gm, '')
      .replace(/^```\n?/gm, '')
      .trim();
  };

  // Print function - fetches server-generated PDF
  const handlePrint = async () => {
    if (!selectedSummary) return;

    setPreparingPrint(true);
    try {
      const response = await api.get(`/discharge-summaries/${selectedSummary.id}/pdf`, {
        responseType: 'blob',
      });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
          }, 500);
        };
      }
    } catch (error) {
      console.error('Error generating PDF for printing:', error);
    } finally {
      setPreparingPrint(false);
    }
  };

  // Email discharge summary - PDF is now generated server-side
  const handleEmailDischargeSummary = async () => {
    if (!selectedSummary) return;

    // Get owner email from horse data - try ownerEmail field first, then owners JSON
    let ownerEmail = selectedSummary.horse?.ownerEmail;
    
    // If not found, try to get from owners JSON field
    if (!ownerEmail && (selectedSummary.horse as any)?.owners) {
      const owners = (selectedSummary.horse as any).owners;
      const parsedOwners = typeof owners === 'string' ? JSON.parse(owners) : owners;
      
      if (Array.isArray(parsedOwners) && parsedOwners.length > 0) {
        ownerEmail = parsedOwners[0]?.email;
      }
    }

    if (!ownerEmail) {
      alert('No email address found for the horse owner. Please add an email address to the owner profile first.');
      return;
    }

    setSendingEmail(true);
    setEmailSent(false);
    try {
      await api.post(`/discharge-summaries/${selectedSummary.id}/email`, {
        recipientEmail: ownerEmail,
      });
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 3000);
      alert('Discharge summary email sent successfully!');
    } catch (error) {
      console.error('Failed to send discharge summary email:', error);
      alert('Failed to send discharge summary email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  // Download PDF function - fetches server-generated PDF
  const handleDownloadPDF = async () => {
    if (!selectedSummary) return;

    setGeneratingPDF(true);
    try {
      const response = await api.get(`/discharge-summaries/${selectedSummary.id}/pdf`, {
        responseType: 'blob',
      });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `Discharge_Summary_${selectedSummary.horse?.name?.replace(/\s+/g, '_') || 'Unknown'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography>Loading discharge summaries...</Typography>
      </Box>
    );
  }

  // Print/View Mode
  if (selectedSummary && !isEditMode) {
    // Read-only content is sanitized inside DischargeSummaryEditor before it
    // reaches ReactQuill (XSS-VULN-01); no need to pre-sanitize here.
    return (
      <Box>
        {/* Visible editor */}
        <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: { xs: 2, sm: 3 } }}>
          <DischargeSummaryEditor
            content={selectedSummary.content || ''}
            onChange={() => {}}
            readOnly={true}
          />

          {/* Attached imaging block — renders in the read-only / finalized
              view. Uses imagingStudiesSnapshot on finalized summaries and
              the live DischargeSummaryStudy rows on drafts. */}
          {resolvedStudies.length > 0 && (
            <AttachedImagingBlock studies={resolvedStudies} />
          )}

          <Box sx={{
            mt: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: { xs: 1.5, sm: 2 }
          }}>
            <Button
              variant="outlined"
              onClick={() => setSelectedSummary(null)}
              fullWidth={isMobile}
              sx={{ 
                minHeight: 44,
                fontSize: { xs: '0.875rem', sm: '0.9rem' },
              }}
            >
              Back to List
            </Button>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 1.5 },
              flexWrap: 'wrap'
            }}>
              {/* DELETE /discharge-summaries/:id requires manage:medical_records_archive
                  since the P3 sweep moved the whole archive family onto one slug — the
                  Edit and Finalize buttons below deliberately STAY on
                  manage:medical_records, which is what PUT /:id and POST /:id/finalize
                  still require. No legacy role's access changes: OWNER, ADMIN,
                  VETERINARIAN and VETERINARY_TECHNICIAN hold both slugs, and a CSR holds
                  neither — the front desk never saw these two buttons. */}
              {hasPermission('manage:medical_records_archive') && selectedSummary.status === 'DRAFT' && (
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth={isMobile}
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this discharge summary?')) {
                      deleteSummary.mutate(selectedSummary.id);
                    }
                  }}
                  sx={{ 
                    minHeight: 44,
                    fontSize: { xs: '0.875rem', sm: '0.9rem' },
                  }}
                >
                  Delete
                </Button>
              )}
              {hasPermission('manage:medical_records') && selectedSummary.status === 'DRAFT' && (
                <Button
                  variant="outlined"
                  startIcon={!isMobile && <SaveIcon />}
                  fullWidth={isMobile}
                  onClick={() => handleEdit(selectedSummary)}
                  sx={{ 
                    minHeight: 44,
                    fontSize: { xs: '0.875rem', sm: '0.9rem' },
                  }}
                >
                  Edit
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={sendingEmail ? <CircularProgress size={18} /> : (!isMobile && <EmailIcon />)}
                fullWidth={isMobile}
                onClick={handleEmailDischargeSummary}
                // Server-side email send — can't be queued/replayed offline.
                disabled={sendingEmail || generatingPDF || preparingPrint || isOffline}
                sx={{ 
                  minHeight: 44,
                  fontSize: { xs: '0.875rem', sm: '0.9rem' },
                }}
              >
                {sendingEmail ? 'Sending...' : emailSent ? 'Email Sent!' : (isMobile ? 'Email' : 'Email Summary')}
              </Button>
              <Button
                variant="outlined"
                startIcon={generatingPDF ? <CircularProgress size={18} /> : (!isMobile && <DownloadIcon />)}
                fullWidth={isMobile}
                onClick={handleDownloadPDF}
                // Server-generated PDF — requires network; disabled offline.
                disabled={generatingPDF || preparingPrint || isOffline}
                sx={{ 
                  minHeight: 44,
                  fontSize: { xs: '0.875rem', sm: '0.9rem' },
                }}
              >
                {generatingPDF ? 'Generating...' : (isMobile ? 'PDF' : 'Download PDF')}
              </Button>
              <Button
                variant="contained"
                startIcon={preparingPrint ? <CircularProgress size={18} /> : (!isMobile && <PrintIcon />)}
                fullWidth={isMobile}
                onClick={handlePrint}
                // Server-generated PDF for printing — requires network.
                disabled={preparingPrint || generatingPDF || isOffline}
                sx={{ 
                  minHeight: 44,
                  fontSize: { xs: '0.875rem', sm: '0.9rem' },
                }}
              >
                {preparingPrint ? 'Preparing...' : 'Print'}
              </Button>
              {hasPermission('manage:medical_records') && selectedSummary.status === 'DRAFT' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={!isMobile && <CheckIcon />}
                  fullWidth={isMobile}
                  onClick={() => finalizeSummary.mutate(selectedSummary.id)}
                  // Finalization does irreversible server-side work (locks the
                  // record, snapshots imaging) that can't be safely replayed —
                  // keep it online-only; disabled while offline.
                  disabled={isOffline}
                  sx={{
                    minHeight: 44,
                    fontSize: { xs: '0.875rem', sm: '0.9rem' },
                  }}
                >
                  Finalize
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Edit Mode
  if (isEditMode) {
    return (
      <Box>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {selectedSummary ? 'Edit Discharge Summary' : 'New Discharge Summary'}
          </Typography>

          <Stack spacing={2}>
            {/* Attached imaging studies — only shown once the summary exists.
                Each chip shows the first thumbnail + date + modality, with
                a remove affordance. "Add studies" opens the picker dialog. */}
            {selectedSummary && (
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                  <ImageIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Imaging Studies
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {attachedStudies.length
                      ? `${attachedStudies.length} attached`
                      : 'None attached — they\'ll render as a thumbnail block on the printed summary'}
                  </Typography>
                  <Box flex={1} />
                  {/* Imaging attach/detach is Keystone-backed (online-only) —
                      disabled offline, span-wrapped so the tooltip still shows. */}
                  <Tooltip title={!isOffline ? '' : 'Imaging attachments need a connection'}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setImagingPickerOpen(true)}
                        disabled={isOffline}
                      >
                        Add studies
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                {attachedStudies.length > 0 && (
                  <>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {attachedStudies.map(s => (
                        <Tooltip
                          key={s.studyId}
                          title={!isOffline ? '' : 'Imaging attachments need a connection'}
                        >
                          <Chip
                            size="medium"
                            icon={<ImageIcon fontSize="small" />}
                            label={
                              <span>
                                {s.studyDate ? formatInstant(s.studyDate, INSTANT_FORMATS.date) : 'Unknown'}
                                {s.modalityName ? ` · ${s.modalityName}` : ''} · Acc #{s.accessionNumber}
                                {s.thumbnails?.length ? ` · ${s.thumbnails.length} thumbs` : ''}
                              </span>
                            }
                            onDelete={!isOffline ? () => detachStudyMutation.mutate(s.studyId) : undefined}
                            deleteIcon={<RemoveIcon />}
                            sx={{ maxWidth: '100%' }}
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                    {/* Live preview of what will render below the content in
                        the finalized summary. Kept compact so it doesn't
                        dominate the edit screen. */}
                    <Box mt={1.5}>
                      <Typography variant="caption" color="text.secondary">
                        Preview — this block will appear below the summary text in the printed PDF:
                      </Typography>
                      <AttachedImagingBlock studies={attachedStudies} compact />
                    </Box>
                  </>
                )}
                <Divider sx={{ mt: 1.5 }} />
              </Box>
            )}

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Discharge Summary Content
              </Typography>
              <DischargeSummaryEditor
                content={formData.content || ''}
                onChange={(content) => setFormData({ ...formData, content })}
              />
            </Box>

            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!formData.content}
              >
                Save
              </Button>
            </Box>
          </Stack>
        </Paper>

        {/* Imaging picker dialog — attach Keystone studies to this summary */}
        {selectedSummary && (
          <ImagingStudyPicker
            open={imagingPickerOpen}
            horseId={horseId}
            admissionDateTime={formData.admissionDateTime}
            dischargeDateTime={formData.dischargeDateTime}
            alreadyAttached={attachedStudies.map(s => s.studyId)}
            onClose={() => setImagingPickerOpen(false)}
            onConfirm={studies => {
              attachStudiesMutation.mutate(studies);
              setImagingPickerOpen(false);
            }}
          />
        )}
      </Box>
    );
  }

  // List Mode
  return (
    <Box>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1.5, sm: 0 }}
        mb={{ xs: 2, sm: 3 }}
      >
        <Typography 
          variant="h5"
          sx={{ 
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <HospitalIcon sx={{ mr: 1, fontSize: { xs: 20, sm: 24 } }} />
          Discharge Summaries
        </Typography>
        <Box 
          display="flex" 
          gap={{ xs: 1, sm: 1.5 }}
          flexDirection={{ xs: 'column', sm: 'row' }}
          width={{ xs: '100%', sm: 'auto' }}
        >
          {/* AI generation runs server-side — never works offline, so the
              notice lives on the button instead of a dead-end modal. */}
          <Tooltip
            title={!isOffline ? '' : 'AI generation needs a connection — use New Discharge Summary to draft offline'}
          >
            <Box component="span" sx={{ display: 'inline-flex', width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                startIcon={!isMobile && <AutoAwesomeIcon />}
                onClick={() => setIsAutomationModalOpen(true)}
                disabled={isOffline}
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  minHeight: 44,
                  fontSize: { xs: '0.875rem', sm: '0.9rem' },
                }}
              >
                {isMobile ? 'Auto' : 'Automation'}
              </Button>
            </Box>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={!isMobile && <AddIcon />}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
            sx={{ 
              minHeight: 44,
              fontSize: { xs: '0.875rem', sm: '0.9rem' },
            }}
            onClick={async () => {
              try {
                // Create a new discharge summary to get the template. Routed through
                // executeWrite so it works offline: ONLINE it hits the server (which
                // generates the template content) — unchanged behavior; OFFLINE it
                // queues an optimistic DRAFT and opens the editor on the temp row
                // (subsequent edits coalesce into the create op).
                const draftPayload = {
                  horseId,
                  status: 'DRAFT' as DischargeSummary['status'],
                  admissionDateTime: new Date().toISOString(),
                  dischargeDateTime: new Date().toISOString(),
                  content: null,
                  attendingVet: null,
                  finalizedAt: null,
                  finalizedBy: null,
                };
                const newSummary = await executeWrite<DischargeSummary>({
                  entityType: 'dischargeSummary',
                  table: 'dischargeSummaries',
                  payload: draftPayload as Record<string, any>,
                  networkFn: () => DischargeSummaryService.create(draftPayload),
                });
                // Refresh the list
                queryClient.invalidateQueries(['discharge-summaries', horseId]);
                // Open it in edit mode
                setSelectedSummary(newSummary);
                setFormData({
                  horseId: newSummary.horseId,
                  status: newSummary.status,
                  admissionDateTime: newSummary.admissionDateTime,
                  dischargeDateTime: newSummary.dischargeDateTime,
                  content: sanitizeDischargeHtml(newSummary.content),
                  attendingVet: newSummary.attendingVet || '',
                  finalizedAt: newSummary.finalizedAt,
                  finalizedBy: newSummary.finalizedBy,
                });
                setIsEditMode(true);
              } catch (error: any) {
                if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
                  alert(error.message);
                  return;
                }
                console.error('Failed to create discharge summary:', error);
              }
            }}
          >
            New Discharge Summary
          </Button>
        </Box>
      </Box>

      {summaries && summaries.length > 0 ? (
        <List>
          {summaries.map((summary: DischargeSummary) => (
            <Paper key={summary.id} sx={{ mb: 2 }}>
              <ListItemButton onClick={() => setSelectedSummary(summary)}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography data-testid="discharge-summary-window">
                        {formatInstant(summary.admissionDateTime, INSTANT_FORMATS.dateTime)}
                        {summary.dischargeDateTime && ` - ${formatInstant(summary.dischargeDateTime, INSTANT_FORMATS.dateTime)}`}
                      </Typography>
                      <Chip 
                        label={summary.status} 
                        color={summary.status === 'FINALIZED' ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={`Attending Vet: ${summary.attendingVet || 'Not specified'}`}
                />
              </ListItemButton>
            </Paper>
          ))}
        </List>
      ) : (
        <Alert severity="info">
          No discharge summaries found. Click "New Discharge Summary" or "Automation" to create one.
        </Alert>
      )}

      <AutomationSuggestionsModal
        open={isAutomationModalOpen}
        onClose={() => setIsAutomationModalOpen(false)}
        horseId={horseId}
        onGenerate={async (selectedIds) => {
          setGeneratingAI(true);
          try {
            const generatedSummary = await DischargeSummaryService.generateWithAI({
              horseId,
              selectedSoapNoteIds: selectedIds.soapNoteIds,
              selectedMedicalRecordIds: selectedIds.medicalRecordIds,
              selectedProcedureIds: selectedIds.procedureIds,
              selectedVaccinationIds: selectedIds.vaccinationIds,
              selectedMedicationIds: selectedIds.medicationIds,
              admissionDateTime: new Date().toISOString(),
            });
            
            queryClient.invalidateQueries(['discharge-summaries', horseId]);
            
            setSelectedSummary(generatedSummary);
            setFormData({
              horseId: generatedSummary.horseId,
              status: generatedSummary.status,
              admissionDateTime: generatedSummary.admissionDateTime,
              dischargeDateTime: generatedSummary.dischargeDateTime,
              content: sanitizeDischargeHtml(sanitizeAIContent(generatedSummary.content || '')),
              attendingVet: generatedSummary.attendingVet || '',
              finalizedAt: generatedSummary.finalizedAt,
              finalizedBy: generatedSummary.finalizedBy,
            });
            setIsEditMode(true);
          } catch (error) {
            console.error('Failed to generate discharge summary:', error);
            alert('Failed to generate discharge summary. Please try again.');
          } finally {
            setGeneratingAI(false);
          }
        }}
      />

      {/* AI Generation Loading Dialog */}
      <Dialog open={generatingAI}>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" p={3}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>Generating Discharge Summary...</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              AI is analyzing medical records and creating a comprehensive summary
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DischargeSummarySimplified;

